import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Card, Form, Button, Alert, Modal, Badge, Image } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faSpinner,
  faPlus,
  faPen,
  faTrash,
  faStar,
  faUpload,
  faFileImage,
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons';

import {
  useFetchOwnProfile,
  useUpdateOwnProfile,
  useUploadOwnSignature,
  useUploadOwnLetterhead,
  useCreateProfile,
  useUpdateProfile,
  useDeleteProfile,
  useSetDefaultProfile,
} from '../../services/provider';
import { useForm } from '../../hooks/useForm';
import { useUser } from '../../contexts/UserContext';
import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';
import Loading from '../common/Loading';
import TimezoneSelect from '../common/TimezoneSelect';
import StateSelect from '../common/StateSelect';
import SignatureInput from '../common/SignatureInput';

const CONTACT_DEFAULTS = {
  userPhone: '',
  userTimezone: 'America/Chicago',
};

const PROFILE_DEFAULTS = {
  profileName: '',
  title: '',
  specialty: '',
  npi: '',
  licenseNumber: '',
  licenseState: '',
  address: '',
  businessPhone: '',
  isDefault: false,
};

const MAX_PROFILES = 3;

// Card styling with hover effects
const cardBaseStyle = {
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  border: 'none',
  borderRadius: '12px',
  transition: 'box-shadow 0.2s ease',
};

const cardHoverStyle = {
  ...cardBaseStyle,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
};

const cardHeaderStyle = {
  backgroundColor: '#252525',
  color: '#f5f5f5',
  borderRadius: '12px 12px 0 0',
};

const cardBodyStyle = {
  backgroundColor: '#efefef',
  borderRadius: '0 0 12px 12px',
};

const ProviderSettings = () => {
  const { updateUser } = useUser();
  const { form: contactForm, onChange: onContactChange, setValues: setContactValues } = useForm(CONTACT_DEFAULTS);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [letterheadUrl, setLetterheadUrl] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const letterheadInputRef = useRef(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredAddProfile, setHoveredAddProfile] = useState(false);

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null); // null for new, object for edit
  const { form: profileForm, onChange: onProfileChange, setValues: setProfileValues } = useForm(PROFILE_DEFAULTS);

  // Delete confirmation state
  const [profileToDelete, setProfileToDelete] = useState(null);

  // Fetch provider's own profile
  const { data: profileData, isLoading: isLoadingProfile, error: profileError } = useFetchOwnProfile();

  // Contact info mutation
  const updateContactInfo = useUpdateOwnProfile(
    () => {
      toast.success('Contact info updated successfully.');
    },
    { onError: (err) => toast.error(err.response?.data?.msg || 'Failed to update contact info') },
  );

  // Signature mutation
  const uploadSignature = useUploadOwnSignature(
    (data) => {
      if (data?.signatureUrl) {
        setSignatureUrl(data.signatureUrl);
        // Update user context so the "signature needed" alert dismisses
        updateUser({ providerSignatureUrl: data.signatureUrl });
      }
      toast.success('Signature saved successfully.');
    },
    { onError: (err) => toast.error(err.response?.data?.msg || 'Failed to upload signature') },
  );

  // Letterhead mutation
  const uploadLetterhead = useUploadOwnLetterhead(
    (data) => {
      if (data?.letterheadUrl) {
        setLetterheadUrl(data.letterheadUrl);
      }
      toast.success('Letterhead saved successfully.');
    },
    { onError: (err) => toast.error(err.response?.data?.msg || 'Failed to upload letterhead') },
  );

  // Profile mutations
  const createProfile = useCreateProfile(
    () => {
      toast.success('Profile created successfully.');
      setShowProfileModal(false);
    },
    { onError: (err) => toast.error(err.response?.data?.msg || 'Failed to create profile') },
  );

  const updateProfile = useUpdateProfile(
    () => {
      toast.success('Profile updated successfully.');
      setShowProfileModal(false);
    },
    { onError: (err) => toast.error(err.response?.data?.msg || 'Failed to update profile') },
  );

  const deleteProfile = useDeleteProfile(
    () => {
      toast.success('Profile deleted successfully.');
      setProfileToDelete(null);
    },
    { onError: (err) => toast.error(err.response?.data?.msg || 'Failed to delete profile') },
  );

  const setDefaultProfile = useSetDefaultProfile(
    () => {
      toast.success('Default profile updated.');
    },
    { onError: (err) => toast.error(err.response?.data?.msg || 'Failed to set default profile') },
  );

  // Populate form when profile data loads
  useEffect(() => {
    if (profileData?.provider) {
      const provider = profileData.provider;
      setContactValues({
        userPhone: provider.userPhone || '',
        userTimezone: provider.userTimezone || 'America/Chicago',
      });
      setSignatureUrl(provider.providerSignatureUrl || null);
      setLetterheadUrl(provider.providerLetterheadUrl || null);
      setProfiles(provider.profiles || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  const handleSignatureSave = (file) => {
    uploadSignature.mutate(file);
  };

  const handleLetterheadChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadLetterhead.mutate(file);
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    updateContactInfo.mutate({
      userPhone: contactForm.userPhone || null,
      userTimezone: contactForm.userTimezone,
    });
  };

  // Profile modal handlers
  const openAddProfile = () => {
    setEditingProfile(null);
    setProfileValues(PROFILE_DEFAULTS);
    setShowProfileModal(true);
  };

  const openEditProfile = (profile) => {
    setEditingProfile(profile);
    setProfileValues({
      profileName: profile.profileName || '',
      title: profile.title || '',
      specialty: profile.specialty || '',
      npi: profile.npi || '',
      licenseNumber: profile.licenseNumber || '',
      licenseState: profile.licenseState || '',
      address: profile.address || '',
      businessPhone: profile.businessPhone || '',
      isDefault: profile.isDefault || false,
    });
    setShowProfileModal(true);
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();

    const payload = {
      profileName: profileForm.profileName,
      title: profileForm.title || null,
      specialty: profileForm.specialty || null,
      npi: profileForm.npi || null,
      licenseNumber: profileForm.licenseNumber,
      licenseState: profileForm.licenseState,
      address: profileForm.address || null,
      businessPhone: profileForm.businessPhone || null,
      isDefault: profileForm.isDefault,
    };

    if (editingProfile) {
      updateProfile.mutate({ profileId: editingProfile.profileId, payload });
    } else {
      createProfile.mutate(payload);
    }
  };

  const handleDeleteProfile = () => {
    if (profileToDelete) {
      deleteProfile.mutate(profileToDelete.profileId);
    }
  };

  const handleSetDefault = (profileId) => {
    setDefaultProfile.mutate(profileId);
  };

  const isLoading = updateContactInfo.isLoading || uploadSignature.isLoading || uploadLetterhead.isLoading;
  const isProfileLoading = createProfile.isLoading || updateProfile.isLoading || deleteProfile.isLoading || setDefaultProfile.isLoading;

  if (isLoadingProfile) {
    return (
      <PrivateLayout>
        <Loading msg="Loading your profile..." />
      </PrivateLayout>
    );
  }

  if (profileError) {
    return (
      <PrivateLayout>
        <Panel>
          <Alert variant="danger">
            Failed to load your profile. Please try refreshing the page.
          </Alert>
          <Button variant="link" as={Link} to="/dashboard">
            Back to Dashboard
          </Button>
        </Panel>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <Row className="align-items-center mb-4">
              <Col>
                <h2 className="mb-1">My Profile</h2>
                <p className="text-muted mb-0">
                  Manage your signature, certification profiles, letterhead, and other general information.
                </p>
              </Col>
            </Row>

            {/* Signature Section */}
            <Card
              className="mb-4"
              style={hoveredCard === 'signature' ? cardHoverStyle : cardBaseStyle}
              onMouseEnter={() => setHoveredCard('signature')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card.Header style={cardHeaderStyle}>
                <h5 className="mb-0">Signature</h5>
              </Card.Header>
              <Card.Body style={cardBodyStyle}>
                <p className="text-muted small mb-3">
                  Your signature will be used when signing DBQ forms. It is shared across all certification profiles.
                </p>
                <SignatureInput
                  onSave={handleSignatureSave}
                  currentSignatureUrl={signatureUrl}
                  disabled={isLoading}
                  isLoading={uploadSignature.isLoading}
                />
              </Card.Body>
            </Card>

            {/* Certification Profiles Section */}
            <Card
              className="mb-4"
              style={hoveredCard === 'profiles' ? cardHoverStyle : cardBaseStyle}
              onMouseEnter={() => setHoveredCard('profiles')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card.Header style={cardHeaderStyle}>
                <h5 className="mb-0">Certification Profiles</h5>
              </Card.Header>
              <Card.Body style={cardBodyStyle}>
                <p className="text-muted small mb-3">
                  Create up to {MAX_PROFILES} certification profiles for different state licenses. When signing a DBQ, you can choose which profile to use.
                </p>

                <Row>
                  {/* Render existing profiles */}
                  {profiles.map((profile) => (
                    <Col md={4} key={profile.profileId} className="mb-3 mb-md-0">
                      <Card className={`h-100 ${profile.isDefault ? 'border-primary' : ''}`}>
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-0">{profile.profileName}</h6>
                            {profile.isDefault && (
                              <Badge bg="primary">
                                <FontAwesomeIcon icon={faStar} className="me-1" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <div className="text-muted small flex-grow-1">
                            {profile.title && <div>{profile.title}</div>}
                            {profile.specialty && <div>{profile.specialty}</div>}
                            {profile.npi && <div>NPI: {profile.npi}</div>}
                            <div className="mt-1">
                              License: {profile.licenseNumber} ({profile.licenseState})
                            </div>
                            {profile.businessPhone && <div>{profile.businessPhone}</div>}
                            {profile.address && <div className="mt-1">{profile.address}</div>}
                          </div>
                          <div className="d-flex gap-2 mt-3 pt-2 border-top">
                            {!profile.isDefault && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleSetDefault(profile.profileId)}
                                disabled={isProfileLoading}
                                title="Set as default"
                              >
                                <FontAwesomeIcon icon={faStarOutline} />
                              </Button>
                            )}
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => openEditProfile(profile)}
                              disabled={isProfileLoading}
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setProfileToDelete(profile)}
                              disabled={isProfileLoading}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}

                  {/* Render empty slots with + button */}
                  {Array.from({ length: MAX_PROFILES - profiles.length }).map((_, index) => (
                    <Col md={4} key={`empty-${index}`} className="mb-3 mb-md-0">
                      <Card
                        className="h-100"
                        style={{
                          borderStyle: 'dashed',
                          borderColor: hoveredAddProfile ? '#0d6efd' : undefined,
                          cursor: 'pointer',
                          minHeight: '180px',
                          transition: 'border-color 0.2s ease, color 0.2s ease',
                        }}
                        onClick={openAddProfile}
                        onMouseEnter={() => setHoveredAddProfile(true)}
                        onMouseLeave={() => setHoveredAddProfile(false)}
                      >
                        <Card.Body
                          className="d-flex flex-column align-items-center justify-content-center"
                          style={{ color: hoveredAddProfile ? '#0d6efd' : '#6c757d' }}
                        >
                          <FontAwesomeIcon icon={faPlus} size="2x" className="mb-2" />
                          <span>Add Profile</span>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>

            {/* General Section */}
            <Card
              className="mb-4"
              style={hoveredCard === 'general' ? cardHoverStyle : cardBaseStyle}
              onMouseEnter={() => setHoveredCard('general')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card.Header style={cardHeaderStyle}>
                <h5 className="mb-0">General</h5>
              </Card.Header>
              <Card.Body style={cardBodyStyle}>
                <form id="contact-form" onSubmit={handleContactSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          name="userPhone"
                          onChange={onContactChange}
                          value={contactForm.userPhone}
                          placeholder="(555) 123-4567"
                          disabled={isLoading}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Timezone</Form.Label>
                        <TimezoneSelect
                          name="userTimezone"
                          onChange={onContactChange}
                          value={contactForm.userTimezone}
                          disabled={isLoading}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="text-end">
                    <Button
                      type="submit"
                      variant="success"
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon
                        icon={updateContactInfo.isLoading ? faSpinner : faCheck}
                        spin={updateContactInfo.isLoading}
                        className="me-2"
                      />
                      Save General
                    </Button>
                  </div>
                </form>
              </Card.Body>
            </Card>

            {/* Letterhead Section */}
            <Card
              className="mb-4"
              style={hoveredCard === 'letterhead' ? cardHoverStyle : cardBaseStyle}
              onMouseEnter={() => setHoveredCard('letterhead')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card.Header style={cardHeaderStyle}>
                <h5 className="mb-0">Letterhead</h5>
              </Card.Header>
              <Card.Body style={cardBodyStyle}>
                <p className="text-muted small mb-3">
                  Upload your logo and preview how your letterhead will appear on Medical Opinions.
                </p>

                <Row>
                  {/* Left side - Logo Upload */}
                  <Col lg={4} className="mb-3 mb-lg-0">
                    <div
                      className="border rounded p-3 text-center"
                      style={{
                        minHeight: '120px',
                        background: letterheadUrl ? '#fff' : '#f8f9fa',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={() => letterheadInputRef.current?.click()}
                    >
                      {uploadLetterhead.isLoading ? (
                        <div className="text-muted">
                          <FontAwesomeIcon icon={faSpinner} spin size="lg" className="mb-2" />
                          <br />
                          <small>Uploading...</small>
                        </div>
                      ) : letterheadUrl ? (
                        <Image
                          src={letterheadUrl}
                          alt="Logo Preview"
                          style={{ maxWidth: '100%', maxHeight: '100px' }}
                        />
                      ) : (
                        <div className="text-muted">
                          <FontAwesomeIcon icon={faFileImage} size="2x" className="mb-2" />
                          <br />
                          <small>Click to upload logo</small>
                        </div>
                      )}
                    </div>
                    <div className="text-center mt-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => letterheadInputRef.current?.click()}
                        disabled={uploadLetterhead.isLoading}
                      >
                        <FontAwesomeIcon icon={faUpload} className="me-1" />
                        {letterheadUrl ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                      <Form.Text className="d-block text-muted mt-1">
                        PNG, JPEG, GIF, WebP (max 5MB)
                      </Form.Text>
                    </div>
                    <input
                      type="file"
                      ref={letterheadInputRef}
                      onChange={handleLetterheadChange}
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      style={{ display: 'none' }}
                    />
                  </Col>

                  {/* Right side - Letterhead Preview */}
                  <Col lg={8}>
                    <div
                      className="border rounded p-4"
                      style={{
                        background: '#fff',
                        minHeight: '120px'
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        {/* Left side - Logo */}
                        <div style={{ minWidth: '150px' }}>
                          {letterheadUrl ? (
                            <Image
                              src={letterheadUrl}
                              alt="Logo"
                              style={{ maxWidth: '150px', maxHeight: '100px' }}
                            />
                          ) : (
                            <div
                              className="border rounded d-flex align-items-center justify-content-center text-muted"
                              style={{ width: '120px', height: '60px', background: '#f8f9fa', fontSize: '0.75rem' }}
                            >
                              Logo
                            </div>
                          )}
                        </div>

                        {/* Right side - Provider Info */}
                        <div className="text-end" style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                          {(() => {
                            const defaultProfile = profiles.find(p => p.isDefault) || profiles[0];
                            const provider = profileData?.provider;
                            return (
                              <>
                                <div style={{ fontWeight: '600' }}>
                                  {provider?.userFirstName || provider?.userLastName
                                    ? `${provider?.userFirstName || ''} ${provider?.userLastName || ''}`.trim()
                                    : 'Examiner Name'}
                                  {defaultProfile?.title && `, ${defaultProfile.title}`}
                                </div>
                                <div className="text-muted">
                                  {defaultProfile?.businessPhone || 'Phone Number'}
                                </div>
                                <div className="text-muted">
                                  {provider?.userEmail || 'email@example.com'}
                                </div>
                                {defaultProfile?.address ? (
                                  defaultProfile.address.split('\n').map((line, idx) => (
                                    <div key={idx} className="text-muted">{line}</div>
                                  ))
                                ) : (
                                  <>
                                    <div className="text-muted">Address Line 1</div>
                                    <div className="text-muted">City, State ZIP</div>
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    <Form.Text className="text-muted">
                      This preview shows how your letterhead will appear on generated PDFs.
                    </Form.Text>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Panel>
        </Col>
      </Row>

      {/* Profile Add/Edit Modal */}
      <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingProfile ? 'Edit Profile' : 'Add Certification Profile'}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleProfileSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Profile Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="profileName"
                onChange={onProfileChange}
                value={profileForm.profileName}
                placeholder="e.g., Texas License, California Practice"
                maxLength={25}
                required
              />
              <Form.Text className="text-muted">
                A name to identify this profile (e.g., state name or practice location)
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Title / Credentials</Form.Label>
                  <Form.Control
                    name="title"
                    onChange={onProfileChange}
                    value={profileForm.title}
                    placeholder="e.g., MD, DO, NP, PA-C, Ph.D"
                  />
                  <Form.Text className="text-muted">
                    Professional designation
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Area of Practice / Specialty</Form.Label>
                  <Form.Control
                    name="specialty"
                    onChange={onProfileChange}
                    value={profileForm.specialty}
                    placeholder="e.g., Psychiatry, Orthopedics"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>NPI Number</Form.Label>
                  <Form.Control
                    name="npi"
                    onChange={onProfileChange}
                    value={profileForm.npi}
                    placeholder="National Provider Identifier"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Business Phone / Fax</Form.Label>
                  <Form.Control
                    type="tel"
                    name="businessPhone"
                    onChange={onProfileChange}
                    value={profileForm.businessPhone}
                    placeholder="(555) 123-4567"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Medical License Number <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="licenseNumber"
                    onChange={onProfileChange}
                    value={profileForm.licenseNumber}
                    placeholder="License #"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>License State <span className="text-danger">*</span></Form.Label>
                  <StateSelect
                    name="licenseState"
                    onChange={onProfileChange}
                    value={profileForm.licenseState}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Business Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="address"
                onChange={onProfileChange}
                value={profileForm.address}
                placeholder="Full business/practice address"
              />
            </Form.Group>

            {profiles.length > 0 && (
              <Form.Check
                type="checkbox"
                id="profile-default"
                name="isDefault"
                label="Set as default profile"
                checked={profileForm.isDefault}
                onChange={(e) => onProfileChange({ target: { name: 'isDefault', value: e.target.checked } })}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowProfileModal(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createProfile.isLoading || updateProfile.isLoading}
            >
              <FontAwesomeIcon
                icon={(createProfile.isLoading || updateProfile.isLoading) ? faSpinner : faCheck}
                spin={createProfile.isLoading || updateProfile.isLoading}
                className="me-2"
              />
              {editingProfile ? 'Save Changes' : 'Create Profile'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={!!profileToDelete} onHide={() => setProfileToDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{profileToDelete?.profileName}</strong>?
          {profileToDelete?.isDefault && profiles.length > 1 && (
            <Alert variant="warning" className="mt-3 mb-0">
              This is your default profile. Another profile will be automatically set as default.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setProfileToDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteProfile}
            disabled={deleteProfile.isLoading}
          >
            <FontAwesomeIcon
              icon={deleteProfile.isLoading ? faSpinner : faTrash}
              spin={deleteProfile.isLoading}
              className="me-2"
            />
            Delete Profile
          </Button>
        </Modal.Footer>
      </Modal>
    </PrivateLayout>
  );
};

export default ProviderSettings;
